<template>
  <div class="word-stats-widget">
    <button class="daily-panel" type="button" @click="handleDailyClick">
      <div v-if="!hideToday" class="panel-title">
        <span v-if="isDailyGoalCelebrated" class="success-check" aria-hidden="true">✅</span>
        {{ $t('wordStats.today') }}
      </div>
      <div class="panel-row">
        <span class="panel-value">
          {{ wordStatsStore.state.dailyWordsLeft }}
          <span v-if="isDailyGoalCelebrated && wordStatsStore.state.dailyWordsLeft===0" class="success-thumb" aria-hidden="true">👍</span>
        </span>
        <span class="panel-label">{{ $t('wordStats.wordsToCollect') }}</span>
      </div>
      <div class="panel-row panel-row-bookmarked">
        <span class="panel-value">{{ wordStatsStore.state.dailyBookmarked }}</span>
        <span class="panel-label">{{ $t('wordStats.wordsBookmarked') }}</span>
      </div>
      <div class="panel-row panel-row-learned">
        <span class="panel-value">{{ wordStatsStore.state.dailyLearned }}</span>
        <span class="panel-label">{{ $t('wordStats.wordsLearned') }}</span>
      </div>
    </button>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useWordStatsStore } from '../../store/word-stats/word-stats-store';
import { useCommonRouteParams } from '../../composables/useCommonRouteParams';

interface Props {
  hideToday?: boolean;
}

withDefaults(defineProps<Props>(), {
  hideToday: false,
});

const emit = defineEmits<{
  (e: 'dailyClick'): void;
}>(); 

const wordStatsStore = useWordStatsStore();
const route = useRoute();
let refreshInterval: number | null = null;
const isDailyGoalCelebrated = computed(
    () => wordStatsStore.state.dailyWordsLeft===0 && wordStatsStore.state.dailyLearned >= 20
);

const handleDailyClick = (): void => {
  emit('dailyClick');
};

onMounted(async () => {
  let mainRouteParams = useCommonRouteParams();
  await wordStatsStore.loadStats(mainRouteParams.nativeLanguage, mainRouteParams.learnedLanguage);
});

watch(
    () => [route.params.nativeLanguage, route.params.learnedLanguage],
    async () => {
      const routeParams = useCommonRouteParams();
      await wordStatsStore.refreshStats(routeParams.nativeLanguage, routeParams.learnedLanguage);
    }
);

onUnmounted(() => {
  if (refreshInterval!==null) {
    clearInterval(refreshInterval);
  }
});
</script>

<style scoped>
.word-stats-widget {
  display: flex;
  flex-direction: column;
  flex-wrap: wrap;
  gap: 8px;
  padding: 0;
  margin: 0;
  align-items: center;
  width: max-content;
}

.daily-panel {
  border: 1px solid #d7dce3;
  border-radius: 14px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  cursor: pointer;
  background: #fff;
  transition: transform 0.2s ease, border-color 0.2s ease;
}

.daily-panel:hover {
  transform: translateY(-1px);
}

.panel-title {
  font-size: 16px;
  color: #1f2937;
  margin-bottom: 2px;
}

.success-check {
  margin-right: 6px;
}

.panel-row {
  display: flex;
  align-items: baseline;
  gap: 6px;
}

.panel-value {
  font-size: var(--font-size-regular);
  color: #1f2937;
}

.success-thumb {
  margin-left: 6px;
}

.panel-label {
  font-size: var(--font-size-regular);
  color: #6b7280;
}

.panel-row-bookmarked .panel-value,
.panel-row-bookmarked .panel-label {
  color: #65a30d;
}

.panel-row-learned .panel-value,
.panel-row-learned .panel-label {
  color: #16a34a;
}

@media (max-width: 640px) {
  .word-stats-widget {
    gap: 8px;
    width: 100%;
  }

  .daily-panel {
    width: 100%;
    min-width: 0;
  }
}

@media (max-width: 768px) {
  .panel-title {
    font-size: var(--font-size-menu);
  }
}
</style>
